const INTEGRATION_APPLICATION_GWS = 'GWS'
const MICROSOFT_GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Cloud Identity Free/Premium cannot stay assigned when a Google Workspace (primary) SKU is added. */
export const GWS_CLOUD_IDENTITY_SKU_IDS = ['1010010001', '1010050001']

/** Vault requires Google Workspace and cannot stay assigned with Cloud Identity. */
export const GWS_VAULT_SKU_IDS = ['Google-Vault', 'Google-Vault-Former-Employee']

/** Google-Apps / Education SKU id prefixes from the Workspace product catalogue. */
export const GWS_PRIMARY_WORKSPACE_SKU_PREFIXES = ['101002', '101006', '101031', 'Google-Apps']

/** Microsoft SKUs and Entra user ids are GUIDs; Google SKUs and Directory user ids are not. */
export const isMicrosoftGuid = (value) => typeof value === 'string' && MICROSOFT_GUID_PATTERN.test(value)

export const getGwsLicenseSkuIds = (ids = []) => (ids || []).filter((id) => id && !isMicrosoftGuid(id))

const looksLikeEmail = (value) => typeof value === 'string' && value.includes('@')

const getGoogleDirectoryId = (employee) => {
  const googleDirectoryId =
    employee?.gwsEmployeeIdentifier ||
    (!isMicrosoftGuid(employee?.employeeIdentifier) ? employee?.employeeIdentifier : null)
  if (googleDirectoryId && !isMicrosoftGuid(googleDirectoryId)) {
    return googleDirectoryId
  }
  return null
}

const getLicenseId = (license) => (typeof license === 'string' ? license : license?.id)

export const isGwsCloudIdentitySku = (skuId) => GWS_CLOUD_IDENTITY_SKU_IDS.includes(skuId)

export const isGwsVaultSku = (skuId) => GWS_VAULT_SKU_IDS.includes(skuId)

export const isGwsPrimaryWorkspaceSku = (skuId) => {
  if (!skuId || isMicrosoftGuid(skuId) || isGwsCloudIdentitySku(skuId) || isGwsVaultSku(skuId)) {
    return false
  }
  return GWS_PRIMARY_WORKSPACE_SKU_PREFIXES.some((prefix) => skuId.startsWith(prefix))
}

const isIncompatibleWithCloudIdentity = (skuId) => isGwsPrimaryWorkspaceSku(skuId) || isGwsVaultSku(skuId)

const pushConflictReplacement = (skuId, nextRemoved, conflictReplacementSkuIds) => {
  if (!nextRemoved.includes(skuId)) {
    nextRemoved.push(skuId)
  }
  if (!conflictReplacementSkuIds.includes(skuId)) {
    conflictReplacementSkuIds.push(skuId)
  }
}

/**
 * Cloud Identity cannot stay assigned with a Workspace SKU or Vault.
 * Adding Vault or a Workspace SKU unassigns Cloud Identity first; adding Cloud Identity
 * unassigns Workspace and Vault. The probe restores those SKUs if the replacement add fails.
 */
export const reconcileGwsLicenseChanges = ({ toBeAdded = [], toBeRemoved = [], currentlyAssigned = [] } = {}) => {
  const addingCloudIdentity = toBeAdded.some(isGwsCloudIdentitySku)
  const nextAdded = addingCloudIdentity ? toBeAdded.filter((skuId) => !isGwsVaultSku(skuId)) : [...toBeAdded]
  const nextRemoved = [...toBeRemoved]
  const conflictReplacementSkuIds = []

  if (nextAdded.some(isGwsPrimaryWorkspaceSku)) {
    currentlyAssigned.forEach((skuId) => {
      if (!isGwsCloudIdentitySku(skuId) || nextAdded.includes(skuId)) {
        return
      }
      pushConflictReplacement(skuId, nextRemoved, conflictReplacementSkuIds)
    })
  }

  if (nextAdded.some(isGwsCloudIdentitySku)) {
    currentlyAssigned.forEach((skuId) => {
      if (!isIncompatibleWithCloudIdentity(skuId) || nextAdded.includes(skuId)) {
        return
      }
      pushConflictReplacement(skuId, nextRemoved, conflictReplacementSkuIds)
    })
  }

  if (nextAdded.some(isGwsVaultSku)) {
    currentlyAssigned.forEach((skuId) => {
      if (!isGwsCloudIdentitySku(skuId) || nextAdded.includes(skuId)) {
        return
      }
      pushConflictReplacement(skuId, nextRemoved, conflictReplacementSkuIds)
    })
  }

  return { toBeAdded: nextAdded, toBeRemoved: nextRemoved, conflictReplacementSkuIds }
}

/**
 * Cloud Identity cannot stay selected with a Google Workspace SKU or Vault.
 * The most recently added exclusive type wins.
 */
export const collapseGwsExclusiveLicenses = (selectedIds = [], previousIds = []) => {
  const selected = selectedIds.filter(Boolean)
  const hasCloudIdentity = selected.some(isGwsCloudIdentitySku)
  const hasWorkspace = selected.some(isGwsPrimaryWorkspaceSku)
  const hasVault = selected.some(isGwsVaultSku)

  const addedCloudIdentity = selected.some((id) => isGwsCloudIdentitySku(id) && !previousIds.includes(id))
  if (addedCloudIdentity) {
    return selected.filter((id) => !isIncompatibleWithCloudIdentity(id))
  }

  const addedWorkspace = selected.some((id) => isGwsPrimaryWorkspaceSku(id) && !previousIds.includes(id))
  if (addedWorkspace) {
    return selected.filter((id) => !isGwsCloudIdentitySku(id))
  }

  const addedVault = selected.some((id) => isGwsVaultSku(id) && !previousIds.includes(id))
  if (addedVault && hasCloudIdentity) {
    return selected.filter((id) => !isGwsCloudIdentitySku(id))
  }

  if (hasCloudIdentity && (hasWorkspace || hasVault)) {
    return selected.filter((id) => !isGwsCloudIdentitySku(id))
  }

  return selected
}

export const buildGwsLicenseTaskInfo = ({ toBeAdded = [], toBeRemoved = [], currentlyAssigned = [] } = {}) => {
  const reconciled = reconcileGwsLicenseChanges({ toBeAdded, toBeRemoved, currentlyAssigned })
  return {
    toBeAdded: reconciled.toBeAdded,
    toBeRemoved: reconciled.toBeRemoved,
    conflictReplacementSkuIds: reconciled.conflictReplacementSkuIds,
  }
}

export const getEffectiveGwsLicenseSelections = ({
  licensesToAdd = [],
  licensesToRemove = [],
  currentlyAssigned = [],
  catalog = [],
} = {}) => {
  const { toBeAdded, toBeRemoved } = reconcileGwsLicenseChanges({
    toBeAdded: licensesToAdd.map(getLicenseId).filter(Boolean),
    toBeRemoved: licensesToRemove.map(getLicenseId).filter(Boolean),
    currentlyAssigned: currentlyAssigned.map(getLicenseId).filter(Boolean),
  })

  const byId = new Map()
  ;[...catalog, ...currentlyAssigned, ...licensesToAdd, ...licensesToRemove].forEach((license) => {
    const id = getLicenseId(license)
    if (id && typeof license === 'object') {
      byId.set(id, license)
    }
  })

  const toLicense = (id) => byId.get(id) || { id, displayName: id }
  return {
    licensesToAdd: toBeAdded.map(toLicense),
    licensesToRemove: toBeRemoved.map(toLicense),
  }
}

/**
 * True when this employee is Google-directory-primary.
 * Dual-integrated GET prefers Entra (GUID employeeIdentifier), so those rows stay Microsoft.
 */
export const isGwsDirectoryEmployee = (employee) => {
  if (!employee) {
    return false
  }
  if (isMicrosoftGuid(employee.employeeIdentifier)) {
    return false
  }
  return Boolean(employee.gwsEmployeeIdentifier || employee.employeeIdentifier)
}

/**
 * Directory Admin SDK accepts a primary email or the numeric user id.
 * Always prefer the stable Google user id so an edited SQL contact email cannot
 * target a different Directory account. Never send an Entra object id.
 */
export const getGwsDirectoryUserId = (employee) => {
  if (!employee) {
    return ''
  }

  const googleDirectoryId = getGoogleDirectoryId(employee)
  if (googleDirectoryId) {
    return googleDirectoryId
  }

  if (looksLikeEmail(employee.gwsPrimaryEmail)) {
    return employee.gwsPrimaryEmail
  }

  return employee.email || ''
}

/**
 * Address shown in password-reset notifications. Directory mutations still use getGwsDirectoryUserId.
 */
export const getGwsNotificationEmail = (employee) => {
  if (!employee) {
    return ''
  }

  if (looksLikeEmail(employee.gwsPrimaryEmail)) {
    return employee.gwsPrimaryEmail
  }

  if (looksLikeEmail(employee.email)) {
    return employee.email
  }

  return ''
}

/**
 * License Manager insert/delete only accepts the user's primary email — not the numeric Directory id.
 * Prefer Directory metadata; GWS-only SQL email is a last resort and is never labeled as gwsPrimaryEmail.
 * Dual-integrated rows must not fall back to the Microsoft UPN.
 */
export const getGwsLicenseUserId = (employee) => {
  if (!employee) {
    return ''
  }

  if (looksLikeEmail(employee.gwsPrimaryEmail)) {
    return employee.gwsPrimaryEmail
  }

  if (isGwsDirectoryEmployee(employee) && looksLikeEmail(employee.email)) {
    return employee.email
  }

  return ''
}

/**
 * Map M365 license reference objects into the licenseType shape used by Engage modals.
 * @param {Array} m365Licenses
 * @returns {Array<{ id: string, displayName: string }>}
 */
export const mapM365LicensesToLicenseType = (m365Licenses = []) =>
  m365Licenses
    .filter((license) => license != null && (license.licence_id || license.id))
    .map((license) => ({
      id: license.licence_id || license.id,
      displayName: license.product_display_name || license.licence_id || license.displayName || '',
    }))

const hasM365LicenseEntries = (m365Licenses) =>
  Array.isArray(m365Licenses) && m365Licenses.some((license) => license != null && (license.licence_id || license.id))

/**
 * Normalize employee license fields for the active integration.
 * Dual-integrated employees may have both m365Licenses and GWS licenseType;
 * never overwrite GWS licenseType with M365 SKUs when GWS is selected.
 *
 * @param {Object} employee
 * @param {string} [integrationApplicationName]
 * @returns {Object}
 */
export const normalizeEmployeeLicenseType = (employee, integrationApplicationName) => {
  if (!employee) return employee

  const isGws = integrationApplicationName === INTEGRATION_APPLICATION_GWS

  if (isGws) {
    // Keep API licenseType as-is when present (including an explicit empty array).
    // Do not invent [] when the field was omitted — merge can keep list-row data.
    return employee
  }

  if (hasM365LicenseEntries(employee.m365Licenses)) {
    return {
      ...employee,
      licenseType: mapM365LicensesToLicenseType(employee.m365Licenses),
    }
  }

  // M365 context: do not fall back to GWS licenseType when m365Licenses is empty
  return {
    ...employee,
    licenseType: [],
  }
}

export const getEmployeeLicenseDisplayNames = (employee, integrationApplicationName) => {
  const isGws = integrationApplicationName === INTEGRATION_APPLICATION_GWS
  if (isGws) {
    return (employee?.licenseType || [])
      .filter((license) => license?.id && !isMicrosoftGuid(license.id))
      .map((license) => license.displayName || license.id)
      .filter(Boolean)
  }
  if (employee?.licenseType?.length) {
    return employee.licenseType.map((license) => license.displayName || license.id).filter(Boolean)
  }

  return mapM365LicensesToLicenseType(employee?.m365Licenses)
    .map((license) => license.displayName)
    .filter(Boolean)
}
